import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, Lock, Mail, Phone, LogIn, Loader2 } from "lucide-react"; // ✅ Loader2 icon for spinner
import { motion } from "framer-motion";
import LogoImg from "../assets/images/goelectronixB.png"; 

const LOGO_URL = "https://yourdomain.com/assets/goelectronix-logo.png";

export default function LoginPage() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [errors, setErrors] = useState({ id: "", pwd: "" });
  const [submitting, setSubmitting] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  // const brandBlue = "#104191"; // example color


  const navigate = useNavigate();

  // ✅ Prevent access to /login if already logged in
  useEffect(() => {
    const authUserData = sessionStorage.getItem("authUserData");
    if (authUserData) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const validate = () => {
    let ok = true;
    let idErr = "";
    let pwdErr = "";

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    const phoneRe = /^[0-9]{10}$/;

    if (!identifier.trim()) {
      idErr = "Enter email or phone";
      ok = false;
    } else if (!(emailRe.test(identifier) || phoneRe.test(identifier))) {
      idErr = "Use valid email or 10-digit phone";
      ok = false;
    }

    if (!password) {
      pwdErr = "Enter password";
      ok = false;
    } else if (password.length < 6) {
      pwdErr = "Min 6 characters";
      ok = false;
    }

    setErrors({ id: idErr, pwd: pwdErr });
    return ok;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        email: identifier,
        password,
        company_id: 5865,
      };

      const response = await axios.post(
        "https://goelectronix.in/api/v1/org_user/login",
        payload
      );

      if (response.data.status === "success" && response.data.data?.length > 0) {
        const userData = response.data.data[0];
        sessionStorage.setItem("authUserData", JSON.stringify(userData));
        navigate("/dashboard");
      } else {
        alert("Invalid credentials. Please try again.");
      }
    } catch (err) {
      console.error(err);
      alert("Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const brandBlue = "#0B5ED7";
  const brandDarkBlue = "#0A3E91";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-3 sm:px-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-md"
      >
        <div className="mb-4 text-center">
          {/* <img
            src={LOGO_URL}
            alt="Logo"
            className="mx-auto h-10 sm:h-12 md:h-14 w-auto"
          /> */}

          <img
            src={LogoImg}
            alt="Logo"
            className="mx-auto h-8 sm:h-10 md:h-8 w-auto"
          />

          <h1
            className="mt-2 text-xl md:text-2xl font-semibold"
            style={{ color: brandDarkBlue }}
          >
            Distributor Panel Login
          </h1>
          {/* <p className="mt-1 text-xs md:text-sm text-slate-500">
            Use your registered email or 10-digit phone
          </p> */}
        </div>

        <div className="rounded-2xl border bg-white shadow-sm p-4 sm:p-6 md:p-7">
          <form onSubmit={onSubmit} className="space-y-3 md:space-y-4">
            <label className="block text-xs md:text-sm font-medium text-slate-600">
              Email or Phone
            </label>
            <div
              className={`relative rounded-xl border ${
                errors.id ? "border-red-400" : "border-slate-200"
              } focus-within:border-slate-400`}
            >
              <span className="absolute left-3 top-2.5 text-slate-400">
                {/^[0-9]/.test(identifier) ? (
                  <Phone size={16} />
                ) : (
                  <Mail size={16} />
                )}
              </span>
              <input
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@example.com or 9876543210"
                className="w-full rounded-xl pl-9 pr-3 py-2 text-sm md:text-base outline-none"
                autoComplete="username"
              />
            </div>
            {errors.id && (
              <p className="text-xs text-red-500 mt-0.5">{errors.id}</p>
            )}

            <label className="block text-xs md:text-sm font-medium text-slate-600">
              Password
            </label>
            <div
              className={`relative rounded-xl border ${
                errors.pwd ? "border-red-400" : "border-slate-200"
              } focus-within:border-slate-400`}
            >
              <span className="absolute left-3 top-2.5 text-slate-400">
                <Lock size={16} />
              </span>
              <input
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-xl pl-9 pr-9 py-2 text-sm md:text-base outline-none"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="absolute right-2 top-2 p-1 rounded-lg text-slate-500 hover:bg-slate-50"
                onClick={() => setShowPwd((s) => !s)}
                aria-label={showPwd ? "Hide password" : "Show password"}
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.pwd && (
              <p className="text-xs text-red-500 mt-0.5">{errors.pwd}</p>
            )}

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs md:text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-3.5 w-3.5 accent-blue-600"
                />
                Remember me
              </label>
              <div>
                {/* Forgot password link */}
                <button
                  onClick={() => setShowPopup(true)}
                  className="text-xs md:text-sm font-medium underline"
                  style={{ color: brandBlue }}
                >
                  Forgot password?
                </button>

                {/* Popup */}
                {showPopup && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full">
                      <h2 className="text-lg font-semibold mb-2">Need Help?</h2>
                      <p className="text-sm text-slate-600 mb-4">
                        Please contact our support team for password assistance.
                      </p>
                      <p className="text-sm font-medium text-blue-600 mb-4">
                        📞 +91 9892580308
                      </p>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setShowPopup(false)}
                          className="px-3 py-1.5 rounded-md bg-slate-200 hover:bg-slate-300 text-sm"
                        >
                          Close
                        </button>
                        <a
                          href="tel:9892580308"
                          className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm"
                        >
                          Contact Now
                        </a>

                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ✅ Loading spinner inside button */}
            <button
              type="submit"
              disabled={submitting}
              className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm md:text-base font-semibold text-white shadow-sm disabled:opacity-70"
              style={{ backgroundColor: brandBlue }}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} /> Signing in…
                </>
              ) : (
                <>
                  <LogIn size={16} /> Sign in
                </>
              )}
            </button>

            <p className="text-[11px] md:text-xs text-slate-500 text-center">
              By continuing you agree to our{" "}
              <a
                className="underline"
                href="https://goelectronix.com/termandcondition.php"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms
              </a>{" "}
              &{" "}
              <a
                className="underline"
                href="https://goelectronix.com/termandcondition.php"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy
              </a>
              .
            </p>

          </form>
        </div>

        <p className="mt-4 text-center text-[11px] md:text-xs text-slate-500">
          © {new Date().getFullYear()} Goelectronix
        </p>
      </motion.div>
    </div>
  );
}


// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import { Eye, EyeOff, Lock, Mail, Phone, LogIn } from "lucide-react";
// import { motion } from "framer-motion";

// const LOGO_URL = "https://yourdomain.com/assets/goelectronix-logo.png";

// export default function LoginPage() {
//   const [identifier, setIdentifier] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPwd, setShowPwd] = useState(false);
//   const [remember, setRemember] = useState(true);
//   const [errors, setErrors] = useState({ id: "", pwd: "" });
//   const [submitting, setSubmitting] = useState(false);

//   const navigate = useNavigate();

//   // ✅ Prevent access to /login if already logged in
//   useEffect(() => {
//     const authUserData = sessionStorage.getItem("authUserData");
//     if (authUserData) {
//       navigate("/dashboard", { replace: true });
//     }
//   }, [navigate]);

//   const validate = () => {
//     let ok = true;
//     let idErr = "";
//     let pwdErr = "";

//     const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
//     const phoneRe = /^[0-9]{10}$/;

//     if (!identifier.trim()) {
//       idErr = "Enter email or phone";
//       ok = false;
//     } else if (!(emailRe.test(identifier) || phoneRe.test(identifier))) {
//       idErr = "Use valid email or 10-digit phone";
//       ok = false;
//     }

//     if (!password) {
//       pwdErr = "Enter password";
//       ok = false;
//     } else if (password.length < 6) {
//       pwdErr = "Min 6 characters";
//       ok = false;
//     }

//     setErrors({ id: idErr, pwd: pwdErr });
//     return ok;
//   };

//   const onSubmit = async (e) => {
//     e.preventDefault();
//     if (!validate()) return;

//     setSubmitting(true);
//     try {
//       const payload = {
//         email: identifier,
//         password,
//         company_id: 5865,
//       };

//       const response = await axios.post(
//         "https://goelectronix.in/api/v1/org_user/login",
//         payload
//       );

//       if (response.data.status === "success" && response.data.data?.length > 0) {
//         const userData = response.data.data[0];
//         sessionStorage.setItem("authUserData", JSON.stringify(userData));
//         navigate("/dashboard");
//       } else {
//         alert("Invalid credentials. Please try again.");
//       }
//     } catch (err) {
//       console.error(err);
//       alert("Login failed. Please try again.");
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   const brandBlue = "#0B5ED7";
//   const brandDarkBlue = "#0A3E91";

//     return (
//         <div className="min-h-screen bg-slate-50 flex items-center justify-center px-3 sm:px-6">
//             <motion.div
//                 initial={{ opacity: 0, y: 16 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 transition={{ duration: 0.35 }}
//                 className="w-full max-w-md"
//             >
//                 <div className="mb-4 text-center">
//                     <img src={LOGO_URL} alt="Logo" className="mx-auto h-10 sm:h-12 md:h-14 w-auto" />
//                     <h1 className="mt-2 text-xl md:text-2xl font-semibold" style={{ color: brandDarkBlue }}>
//                         Distributor Panel Login
//                     </h1>
//                     <p className="mt-1 text-xs md:text-sm text-slate-500">Use your registered email or 10-digit phone</p>
//                 </div>

//                 <div className="rounded-2xl border bg-white shadow-sm p-4 sm:p-6 md:p-7">
//                     <form onSubmit={onSubmit} className="space-y-3 md:space-y-4">
//                         <label className="block text-xs md:text-sm font-medium text-slate-600">Email or Phone</label>
//                         <div className={`relative rounded-xl border ${errors.id ? "border-red-400" : "border-slate-200"} focus-within:border-slate-400`}>
//                             <span className="absolute left-3 top-2.5 text-slate-400">
//                                 {/^[0-9]/.test(identifier) ? <Phone size={16} /> : <Mail size={16} />}
//                             </span>
//                             <input
//                                 value={identifier}
//                                 onChange={(e) => setIdentifier(e.target.value)}
//                                 placeholder="you@example.com or 9876543210"
//                                 className="w-full rounded-xl pl-9 pr-3 py-2 text-sm md:text-base outline-none"
//                                 autoComplete="username"
//                             />
//                         </div>
//                         {errors.id && <p className="text-xs text-red-500 mt-0.5">{errors.id}</p>}

//                         <label className="block text-xs md:text-sm font-medium text-slate-600">Password</label>
//                         <div className={`relative rounded-xl border ${errors.pwd ? "border-red-400" : "border-slate-200"} focus-within:border-slate-400`}>
//                             <span className="absolute left-3 top-2.5 text-slate-400">
//                                 <Lock size={16} />
//                             </span>
//                             <input
//                                 type={showPwd ? "text" : "password"}
//                                 value={password}
//                                 onChange={(e) => setPassword(e.target.value)}
//                                 placeholder="••••••••"
//                                 className="w-full rounded-xl pl-9 pr-9 py-2 text-sm md:text-base outline-none"
//                                 autoComplete="current-password"
//                             />
//                             <button
//                                 type="button"
//                                 className="absolute right-2 top-2 p-1 rounded-lg text-slate-500 hover:bg-slate-50"
//                                 onClick={() => setShowPwd((s) => !s)}
//                                 aria-label={showPwd ? "Hide password" : "Show password"}
//                             >
//                                 {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
//                             </button>
//                         </div>
//                         {errors.pwd && <p className="text-xs text-red-500 mt-0.5">{errors.pwd}</p>}

//                         <div className="flex items-center justify-between">
//                             <label className="flex items-center gap-2 text-xs md:text-sm text-slate-600">
//                                 <input
//                                     type="checkbox"
//                                     checked={remember}
//                                     onChange={(e) => setRemember(e.target.checked)}
//                                     className="h-3.5 w-3.5 accent-blue-600"
//                                 />
//                                 Remember me
//                             </label>
//                             <a href="#" className="text-xs md:text-sm font-medium" style={{ color: brandBlue }}>Forgot password?</a>
//                         </div>

//                         <button
//                             type="submit"
//                             disabled={submitting}
//                             className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm md:text-base font-semibold text-white shadow-sm disabled:opacity-70"
//                             style={{ backgroundColor: brandBlue }}
//                         >
//                             <LogIn size={16} /> {submitting ? "Signing in…" : "Sign in"}
//                         </button>

//                         <p className="text-[11px] md:text-xs text-slate-500 text-center">
//                             By continuing you agree to our <a className="underline" href="#">Terms</a> & <a className="underline" href="#">Privacy</a>.
//                         </p>
//                     </form>
//                 </div>

//                 <p className="mt-4 text-center text-[11px] md:text-xs text-slate-500">© {new Date().getFullYear()} Goelectronix</p>
//             </motion.div>
//         </div>
//     );
// }



// ************************ 25 SEPT 2025 ORIGINAL CODE PHASE 1 ************************************************************


// import React, { useState } from "react";
// import { Eye, EyeOff, Lock, Mail, Phone, LogIn } from "lucide-react";
// import { motion } from "framer-motion";

// // =====================================
// // Distributor Panel – Standalone Login (JSX)
// // - Light theme, mobile-first, brand colors
// // - Email/Phone + Password + Remember Me
// // - Show/Hide password, basic validation, error display
// // =====================================

// // Replace with your actual distributor logo URL
// const LOGO_URL = "https://yourdomain.com/assets/goelectronix-logo.png";

// export default function LoginPage() {
//   const [identifier, setIdentifier] = useState(""); // email or phone
//   const [password, setPassword] = useState("");
//   const [showPwd, setShowPwd] = useState(false);
//   const [remember, setRemember] = useState(true);
//   const [errors, setErrors] = useState({ id: "", pwd: "" });
//   const [submitting, setSubmitting] = useState(false);

//   const validate = () => {
//     let ok = true;
//     let idErr = "";
//     let pwdErr = "";

//     const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
//     const phoneRe = /^[0-9]{10}$/; // simple 10-digit phone check

//     if (!identifier.trim()) {
//       idErr = "Enter email or phone";
//       ok = false;
//     } else if (!(emailRe.test(identifier) || phoneRe.test(identifier))) {
//       idErr = "Use valid email or 10-digit phone";
//       ok = false;
//     }

//     if (!password) {
//       pwdErr = "Enter password";
//       ok = false;
//     } else if (password.length < 6) {
//       pwdErr = "Min 6 characters";
//       ok = false;
//     }

//     setErrors({ id: idErr, pwd: pwdErr });
//     return ok;
//   };

//   const onSubmit = async (e) => {
//     e.preventDefault();
//     if (!validate()) return;
//     setSubmitting(true);
//     try {
//       await new Promise((r) => setTimeout(r, 600));
//       alert("Login success! Redirecting to Distributor Panel…");
//       // Example: redirect to dashboard
//       // window.location.href = "/panel";
//     } catch (err) {
//       alert("Login failed. Please try again.");
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   const brandBlue = "#0B5ED7";
//   const brandDarkBlue = "#0A3E91";

//   return (
//     <div className="min-h-screen bg-slate-50 flex items-center justify-center px-3 sm:px-6">
//       <motion.div
//         initial={{ opacity: 0, y: 16 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 0.35 }}
//         className="w-full max-w-md"
//       >
//         <div className="mb-4 text-center">
//           <img src={LOGO_URL} alt="Logo" className="mx-auto h-10 sm:h-12 md:h-14 w-auto" />
//           <h1 className="mt-2 text-xl md:text-2xl font-semibold" style={{ color: brandDarkBlue }}>
//             Distributor Panel Login
//           </h1>
//           <p className="mt-1 text-xs md:text-sm text-slate-500">Use your registered email or 10-digit phone</p>
//         </div>

//         <div className="rounded-2xl border bg-white shadow-sm p-4 sm:p-6 md:p-7">
//           <form onSubmit={onSubmit} className="space-y-3 md:space-y-4">
//             {/* Identifier */}
//             <label className="block text-xs md:text-sm font-medium text-slate-600">Email or Phone</label>
//             <div className={`relative rounded-xl border ${errors.id ? "border-red-400" : "border-slate-200"} focus-within:border-slate-400`}>
//               <span className="absolute left-3 top-2.5 text-slate-400">
//                 {/^[0-9]/.test(identifier) ? <Phone size={16} /> : <Mail size={16} />}
//               </span>
//               <input
//                 value={identifier}
//                 onChange={(e) => setIdentifier(e.target.value)}
//                 placeholder="you@example.com or 9876543210"
//                 className="w-full rounded-xl pl-9 pr-3 py-2 text-sm md:text-base outline-none"
//                 autoComplete="username"
//               />
//             </div>
//             {errors.id && <p className="text-xs text-red-500 mt-0.5">{errors.id}</p>}

//             {/* Password */}
//             <label className="block text-xs md:text-sm font-medium text-slate-600">Password</label>
//             <div className={`relative rounded-xl border ${errors.pwd ? "border-red-400" : "border-slate-200"} focus-within:border-slate-400`}>
//               <span className="absolute left-3 top-2.5 text-slate-400">
//                 <Lock size={16} />
//               </span>
//               <input
//                 type={showPwd ? "text" : "password"}
//                 value={password}
//                 onChange={(e) => setPassword(e.target.value)}
//                 placeholder="••••••••"
//                 className="w-full rounded-xl pl-9 pr-9 py-2 text-sm md:text-base outline-none"
//                 autoComplete="current-password"
//               />
//               <button
//                 type="button"
//                 className="absolute right-2 top-2 p-1 rounded-lg text-slate-500 hover:bg-slate-50"
//                 onClick={() => setShowPwd((s) => !s)}
//                 aria-label={showPwd ? "Hide password" : "Show password"}
//               >
//                 {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
//               </button>
//             </div>
//             {errors.pwd && <p className="text-xs text-red-500 mt-0.5">{errors.pwd}</p>}

//             {/* Options */}
//             <div className="flex items-center justify-between">
//               <label className="flex items-center gap-2 text-xs md:text-sm text-slate-600">
//                 <input
//                   type="checkbox"
//                   checked={remember}
//                   onChange={(e) => setRemember(e.target.checked)}
//                   className="h-3.5 w-3.5 accent-blue-600"
//                 />
//                 Remember me
//               </label>
//               <a href="#" className="text-xs md:text-sm font-medium" style={{ color: brandBlue }}>Forgot password?</a>
//             </div>

//             {/* Submit */}
//             <button
//               type="submit"
//               disabled={submitting}
//               className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm md:text-base font-semibold text-white shadow-sm disabled:opacity-70"
//               style={{ backgroundColor: brandBlue }}
//             >
//               <LogIn size={16} /> {submitting ? "Signing in…" : "Sign in"}
//             </button>

//             {/* Hint */}
//             <p className="text-[11px] md:text-xs text-slate-500 text-center">
//               By continuing you agree to our <a className="underline" href="#">Terms</a> & <a className="underline" href="#">Privacy</a>.
//             </p>
//           </form>
//         </div>

//         {/* Footer */}
//         <p className="mt-4 text-center text-[11px] md:text-xs text-slate-500">© {new Date().getFullYear()} Goelectronix</p>
//       </motion.div>
//     </div>
//   );
// }
